import { useState, useLayoutEffect, useEffect } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import {
  Mail,
  MapPin,
  MessageSquare,
  Send,
  Clock,
  ArrowUpRight,
  Briefcase,
  Loader2,
  CheckCircle2,
  Sparkles,
  Phone,
} from "lucide-react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SEO, seoConfig } from "@/components/SEO";
import { CONTACT_EMAIL } from "@/lib/brand";
import {
  buildEnquiryMessage,
  submitContactForm,
  type ContactPrefill,
} from "@/lib/contact";
import { useSiteLanguage } from "@/lib/useSiteLanguage";
import { contactCopy } from "@/lib/siteCopy";
import type { SiteLanguageCode } from "@/lib/language";

const ease = [0.23, 1, 0.32, 1] as const;
type FormState = {
  name: string;
  email: string;
  phone: string;
  company: string;
  service: string;
  query: string;
  message: string;
};

const emptyForm: FormState = {
  name: "",
  email: "",
  phone: "",
  company: "",
  service: "",
  query: "",
  message: "",
};

function prefillFromState(prefill?: ContactPrefill | null): Partial<FormState> {
  if (!prefill) return {};
  const service =
    prefill.service || (prefill.category ? prefill.category : "") || "";
  const query =
    prefill.query ||
    (prefill.service
      ? `Inquiry · ${prefill.service}`
      : prefill.category
        ? `Inquiry · ${prefill.category}`
        : "");
  const message =
    prefill.message ||
    (prefill.service
      ? buildEnquiryMessage(prefill.service)
      : prefill.category
        ? buildEnquiryMessage(prefill.category)
        : "");

  return { service, query, message };
}

function SuccessState({
  reduceMotion,
  onReset,
  t,
}: {
  reduceMotion: boolean | null;
  onReset: () => void;
  t: (typeof contactCopy)[SiteLanguageCode];
}) {
  return (
    <motion.div
      key="success"
      initial={reduceMotion ? false : { opacity: 0, scale: 0.96, y: 12 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      exit={reduceMotion ? undefined : { opacity: 0, scale: 0.98, y: -8 }}
      transition={{ duration: 0.45, ease }}
      className="relative flex min-h-[380px] flex-col items-center justify-center overflow-hidden text-center"
    >
      {!reduceMotion && (
        <>
          <motion.div
            aria-hidden
            className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_center,_oklch(var(--muted)_/_0.7),_transparent_65%)]"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6 }}
          />
          <motion.div
            aria-hidden
            className="pointer-events-none absolute h-40 w-40 rounded-full bg-emerald-400/15 blur-3xl dark:bg-emerald-500/10"
            animate={{ scale: [1, 1.15, 1], opacity: [0.5, 0.85, 0.5] }}
            transition={{ duration: 3.2, repeat: Infinity, ease: "easeInOut" }}
          />
        </>
      )}

      <div className="relative z-10 flex flex-col items-center px-2">
        <motion.div
          className="relative mb-6 flex h-16 w-16 items-center justify-center"
          initial={reduceMotion ? false : { scale: 0.6, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ type: "spring", stiffness: 320, damping: 18, delay: 0.05 }}
        >
          {!reduceMotion && (
            <motion.span
              aria-hidden
              className="absolute inset-0 rounded-full border border-emerald-500/30"
              initial={{ scale: 0.8, opacity: 0.8 }}
              animate={{ scale: 1.55, opacity: 0 }}
              transition={{ duration: 1.1, ease: "easeOut", delay: 0.15 }}
            />
          )}
          <span className="flex h-16 w-16 items-center justify-center rounded-full border border-emerald-500/25 bg-emerald-500/10 text-emerald-700 shadow-sm dark:text-emerald-400">
            <CheckCircle2 className="h-7 w-7" strokeWidth={1.75} />
          </span>
        </motion.div>

        <motion.h2
          className="text-xl font-bold tracking-tight text-foreground md:text-2xl"
          initial={reduceMotion ? false : { opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.18, ease }}
        >
          {t.successTitle}
        </motion.h2>

        <motion.p
          className="mt-3 max-w-sm text-sm leading-relaxed text-muted-foreground md:text-[15px]"
          initial={reduceMotion ? false : { opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.28, ease }}
        >
          {t.successBody}
        </motion.p>

        <motion.div
          className="mt-8 flex flex-col items-center gap-3 sm:flex-row"
          initial={reduceMotion ? false : { opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.38, ease }}
        >
          <Button
            type="button"
            className="rounded-full px-6"
            onClick={onReset}
          >
            {t.sendAnother}
          </Button>
          <Button
            type="button"
            variant="outline"
            className="rounded-full px-6"
            asChild
          >
            <Link to="/">{t.backHome}</Link>
          </Button>
        </motion.div>
      </div>
    </motion.div>
  );
}

export function ContactPage() {
  const reduceMotion = useReducedMotion();
  const location = useLocation();
  const navigate = useNavigate();
  const lang = useSiteLanguage();
  const t = contactCopy[lang];
  const prefill = (location.state as ContactPrefill | null) || null;

  const contactDetails = [
    {
      icon: Mail,
      label: t.email,
      value: CONTACT_EMAIL,
      href: `mailto:${CONTACT_EMAIL}`,
    },
    {
      icon: MapPin,
      label: t.location,
      value: t.locationValue,
      href: null,
    },
    {
      icon: Clock,
      label: t.response,
      value: t.responseValue,
      href: null,
    },
  ];

  const [formData, setFormData] = useState<FormState>(() => ({
    ...emptyForm,
    ...prefillFromState(prefill),
  }));
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useLayoutEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const next = (location.state as ContactPrefill | null) || null;
    if (!next) return;
    setFormData((prev) => ({
      ...prev,
      ...prefillFromState(next),
    }));
    setSubmitted(false);
    setError(null);
  }, [location.state, location.key]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await submitContactForm({
        ...formData,
        source: prefill?.serviceId
          ? `service:${prefill.serviceId}`
          : "contact-page",
      });
      setSubmitted(true);
      setFormData(emptyForm);
      navigate("/contact", { replace: true, state: null });
    } catch {
      setError(t.errorSend);
    } finally {
      setSubmitting(false);
    }
  };

  const fade = reduceMotion
    ? undefined
    : {
        initial: { opacity: 0, y: 24 },
        animate: { opacity: 1, y: 0 },
        transition: { duration: 0.55, ease },
      };

  const hasServiceContext = Boolean(formData.service || formData.query);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <SEO {...seoConfig.contact} />

      <section className="relative overflow-hidden pt-28 pb-14 md:pt-36 md:pb-16 lg:pt-40">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top,_oklch(var(--muted)_/_0.55),_transparent_55%)]"
        />
        <div
          aria-hidden
          className="pointer-events-none absolute -left-24 top-24 h-64 w-64 rounded-full bg-sky-400/10 blur-3xl dark:bg-sky-500/10"
        />
        <div
          aria-hidden
          className="pointer-events-none absolute -right-20 bottom-0 h-56 w-56 rounded-full bg-amber-400/10 blur-3xl dark:bg-amber-500/10"
        />

        <div className="relative mx-auto max-w-6xl px-4 sm:px-6">
          <motion.div {...fade} className="mx-auto max-w-2xl text-center">
            <span className="mb-5 inline-flex items-center gap-2 text-[11px] font-medium uppercase tracking-[0.22em] text-muted-foreground">
              <MessageSquare className="h-3.5 w-3.5" strokeWidth={1.75} />
              {t.eyebrow}
            </span>
            <h1 className="text-balance text-3xl font-bold tracking-tight text-foreground sm:text-4xl md:text-5xl">
              {t.title}
            </h1>
            <p className="mx-auto mt-5 max-w-xl text-pretty text-[15px] leading-relaxed text-muted-foreground md:text-base">
              {t.subtitle}
            </p>
            {hasServiceContext && (
              <div className="mt-6 inline-flex max-w-full items-center gap-2 rounded-full border border-border bg-card px-4 py-2 text-left text-sm text-foreground shadow-sm">
                <Briefcase className="h-3.5 w-3.5 shrink-0 text-sky-600 dark:text-sky-400" />
                <span className="truncate">
                  {formData.service || formData.query}
                </span>
              </div>
            )}
          </motion.div>
        </div>
      </section>

      <section className="pb-20 md:pb-28 lg:pb-32">
        <div className="mx-auto grid max-w-6xl gap-10 px-4 sm:px-6 lg:grid-cols-[0.9fr_1.1fr] lg:gap-14">
          <motion.aside
            {...(reduceMotion
              ? {}
              : {
                  initial: { opacity: 0, y: 20 },
                  animate: { opacity: 1, y: 0 },
                  transition: { duration: 0.55, ease, delay: 0.08 },
                })}
            className="flex flex-col gap-5"
          >
            <div className="rounded-[1.5rem] border border-border bg-card p-7 md:p-8">
              <h2 className="text-sm font-semibold tracking-tight text-foreground">
                {t.channels}
              </h2>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                {t.channelsBody}
              </p>

              <ul className="mt-7 space-y-5">
                {contactDetails.map(({ icon: Icon, label, value, href }) => (
                  <li key={label} className="flex items-start gap-3.5">
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-border bg-muted text-foreground">
                      <Icon className="h-4 w-4" strokeWidth={1.5} />
                    </span>
                    <div className="min-w-0 pt-0.5">
                      <p className="text-[11px] font-medium uppercase tracking-[0.16em] text-muted-foreground">
                        {label}
                      </p>
                      {href ? (
                        <a
                          href={href}
                          className="mt-1 inline-flex items-center gap-1.5 text-sm font-medium text-foreground transition-colors hover:text-muted-foreground"
                        >
                          {value}
                          <ArrowUpRight className="h-3.5 w-3.5" />
                        </a>
                      ) : (
                        <p className="mt-1 text-sm font-medium text-foreground">
                          {value}
                        </p>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            </div>

            <div className="relative overflow-hidden rounded-[1.5rem] border border-border bg-gradient-to-br from-sky-50 via-background to-amber-50/70 p-7 md:p-8 dark:from-sky-950/35 dark:via-card dark:to-amber-950/25">
              <Sparkles
                className="mb-3 h-4 w-4 text-amber-600 dark:text-amber-400"
                strokeWidth={1.75}
              />
              <h3 className="text-sm font-semibold tracking-tight text-foreground">
                {t.whatHappensNext}
              </h3>
              <ul className="mt-4 space-y-2.5 text-sm leading-relaxed text-muted-foreground">
                {t.whatHappensNextBullets.map((bullet) => (
                  <li key={bullet} className="flex gap-2.5">
                    <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-foreground/50" />
                    {bullet}
                  </li>
                ))}
              </ul>
            </div>
          </motion.aside>

          <motion.div
            {...(reduceMotion
              ? {}
              : {
                  initial: { opacity: 0, y: 20 },
                  animate: { opacity: 1, y: 0 },
                  transition: { duration: 0.55, ease, delay: 0.14 },
                })}
            className="rounded-[1.75rem] border border-border bg-card p-7 shadow-[0_16px_40px_-28px_rgba(0,0,0,0.2)] md:rounded-[2rem] md:p-9 lg:p-10 dark:shadow-none"
          >
            <AnimatePresence mode="wait">
              {submitted ? (
                <SuccessState
                  reduceMotion={reduceMotion}
                  onReset={() => setSubmitted(false)}
                  t={t}
                />
              ) : (
                <motion.form
                  key="form"
                  onSubmit={handleSubmit}
                  className="space-y-5"
                  initial={reduceMotion ? false : { opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={
                    reduceMotion
                      ? undefined
                      : { opacity: 0, y: -10, transition: { duration: 0.25 } }
                  }
                  transition={{ duration: 0.35, ease }}
                >
                  <div>
                    <h2 className="text-lg font-semibold tracking-tight text-foreground">
                      {t.sendAMessage}
                    </h2>
                    <p className="mt-1.5 text-sm text-muted-foreground">
                      {t.formSubtitle}
                    </p>
                  </div>

                  <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor="name">{t.name}</Label>
                      <Input
                        id="name"
                        placeholder={t.namePlaceholder}
                        value={formData.name}
                        onChange={(e) =>
                          setFormData({ ...formData, name: e.target.value })
                        }
                        required
                        className="h-12 rounded-xl"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email">{t.email}</Label>
                      <Input
                        id="email"
                        type="email"
                        placeholder={t.emailPlaceholder}
                        value={formData.email}
                        onChange={(e) =>
                          setFormData({ ...formData, email: e.target.value })
                        }
                        required
                        className="h-12 rounded-xl"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor="phone">{t.phone}</Label>
                      <div className="relative">
                        <Phone className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                        <Input
                          id="phone"
                          type="tel"
                          inputMode="tel"
                          placeholder={t.phonePlaceholder}
                          value={formData.phone}
                          onChange={(e) =>
                            setFormData({ ...formData, phone: e.target.value })
                          }
                          required
                          className="h-12 rounded-xl pl-10"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="company">{t.company}</Label>
                      <Input
                        id="company"
                        placeholder={t.companyPlaceholder}
                        value={formData.company}
                        onChange={(e) =>
                          setFormData({ ...formData, company: e.target.value })
                        }
                        className="h-12 rounded-xl"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor="service">{t.service}</Label>
                      <Input
                        id="service"
                        placeholder={t.servicePlaceholder}
                        value={formData.service}
                        onChange={(e) =>
                          setFormData({ ...formData, service: e.target.value })
                        }
                        className="h-12 rounded-xl"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="query">{t.query}</Label>
                      <Input
                        id="query"
                        placeholder={t.queryPlaceholder}
                        value={formData.query}
                        onChange={(e) =>
                          setFormData({ ...formData, query: e.target.value })
                        }
                        className="h-12 rounded-xl"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="message">{t.message}</Label>
                    <Input
                      id="message"
                      placeholder={t.messagePlaceholder}
                      value={formData.message}
                      onChange={(e) =>
                        setFormData({ ...formData, message: e.target.value })
                      }
                      required
                      className="h-12 rounded-xl"
                    />
                  </div>

                  {error && (
                    <p className="rounded-xl border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive">
                      {error}
                    </p>
                  )}

                  <Button
                    type="submit"
                    size="lg"
                    disabled={submitting}
                    className="group w-full rounded-full px-8 py-6 sm:w-auto"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        {t.sending}
                      </>
                    ) : (
                      <>
                        <Mail className="mr-2 h-4 w-4" />
                        {t.send}
                        <Send className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
                      </>
                    )}
                  </Button>
                </motion.form>
              )}
            </AnimatePresence>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
